import { useNavigate } from "react-router-dom";
import { useCallback } from "react";
import { toast } from "react-toastify";
import { handleAuthenticationFailure } from "../AuthenticationData.ts";

/**
 * Custom hook to handle authentication errors consistently across the application
 * @returns {Function} handleAuthError - Function to handle authentication errors
 */
export const useAuthErrorHandler = () => {
  const navigate = useNavigate();

  const handleAuthError = useCallback(
    (error) => {
      // Check if the error indicates authentication failure
      if (
        error?.redirectToLogin ||
        error?.response?.status === 401 ||
        error?.message?.includes("Session expired") ||
        error?.message?.includes("Unauthorized")
      ) {
        // Check if we're on a public page where 401s might be expected
        const currentPath = window.location.pathname;
        const publicPaths = [
          "/Login",
          "/Signup",
          "/ForgotPasswordEmail",
          "/VerifyMail",
          "/VerifyingMail",
          "/VerifyPasswordChanging",
          "/Profile",
          "/profile",
        ];
        const isOnPublicPage = publicPaths.some((path) =>
          currentPath.startsWith(path)
        );

        // Only handle as auth failure if we're on a protected page or have tokens
        if (!isOnPublicPage || error?.redirectToLogin) {
          handleAuthenticationFailure(navigate, toast);
          return true; // Indicates the error was handled
        } else {
          // On public pages, just log the error without triggering auth failure handling
          return false;
        }
      }
      return false; // Indicates the error was not an auth error
    },
    [navigate]
  );

  return { handleAuthError };
};

export default useAuthErrorHandler;
